(() => {
  const config = window.CTCurrencyConfig || {};
  const shopCurrency = String(config.shopCurrency || 'EUR').toUpperCase();
  const supported = (config.supportedCurrencies || [shopCurrency])
    .map(code => String(code).trim().toUpperCase())
    .filter(Boolean);
  const defaultCurrency = supported.includes(String(config.defaultCurrency || '').toUpperCase())
    ? String(config.defaultCurrency).toUpperCase()
    : shopCurrency;
  const storageKey = 'ct_display_currency';
  const locale = config.locale || document.documentElement.lang || 'fr-FR';
  const currencyDisplay = config.format === 'symbol' ? 'symbol' : 'code';

  const fallbackSelectors = [
    '[data-cart-item-original-price]',
    '[data-cart-item-final-price]',
    '[data-cart-item-regular-price]',
    '[data-cart-item-discount-amount]',
    '.cart-price',
    '.js-cart-subtotal',
    '.hotspot__product-price .onsale',
    '.hotspot__product-price .was'
  ].join(',');

  let activeCurrency = defaultCurrency;

  function getSavedCurrency() {
    try {
      const value = localStorage.getItem(storageKey);
      return supported.includes(value) ? value : null;
    } catch (e) {
      return null;
    }
  }

  function saveCurrency(code) {
    try { localStorage.setItem(storageKey, code); } catch (e) {}
  }

  function parseStoreAmount(text) {
    let value = String(text || '').replace(/\u00a0/g, ' ').trim();
    value = value.replace(/[^0-9,.-]/g, '');
    if (!value) return null;

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    let decimal = null;

    if (lastComma > -1 && lastDot > -1) {
      decimal = lastComma > lastDot ? ',' : '.';
    } else if (lastComma > -1 && value.length - lastComma - 1 === 2) {
      decimal = ',';
    } else if (lastDot > -1 && value.length - lastDot - 1 === 2) {
      decimal = '.';
    }

    if (decimal === ',') {
      value = value.replace(/\./g, '').replace(',', '.');
    } else if (decimal === '.') {
      value = value.replace(/,/g, '');
    } else {
      value = value.replace(/[.,]/g, '');
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  function formatAmount(cents, currency) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function formatShopAmount(cents) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: shopCurrency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function convertCents(cents, toCurrency) {
    if (toCurrency === shopCurrency) return cents;
    if (!window.Currency || typeof window.Currency.convert !== 'function') return null;
    const converted = window.Currency.convert(cents, shopCurrency, toCurrency);
    return Number.isFinite(converted) ? Math.round(converted) : null;
  }

  function renderCorePrice(el) {
    if (!el.dataset.ctOriginalHtml) el.dataset.ctOriginalHtml = el.innerHTML;
    const base = Number(el.dataset.baseCents);
    if (!Number.isFinite(base)) return;

    if (activeCurrency === shopCurrency) {
      const rendered = formatShopAmount(base);
      if (el.textContent.trim() !== rendered || el.dataset.ctRenderedCurrency !== shopCurrency) {
        el.textContent = rendered;
      }
      el.dataset.ctRenderedCurrency = shopCurrency;
      el.dataset.ctRenderedText = rendered;
      return;
    }

    const converted = convertCents(base, activeCurrency);
    if (converted === null) return;
    const rendered = formatAmount(converted, activeCurrency);
    if (el.textContent.trim() === rendered && el.dataset.ctRenderedCurrency === activeCurrency) return;
    el.textContent = rendered;
    el.dataset.ctRenderedCurrency = activeCurrency;
    el.dataset.ctRenderedText = rendered;
  }

  function renderFallbackPrice(el) {
    const currentText = el.textContent.trim();

    if (!el.dataset.ctBaseCents || (el.dataset.ctRenderedText && currentText !== el.dataset.ctRenderedText)) {
      const parsed = parseStoreAmount(currentText);
      if (parsed === null) return;
      el.dataset.ctBaseCents = String(parsed);
      el.dataset.ctOriginalHtml = el.innerHTML;
      delete el.dataset.ctRenderedText;
      delete el.dataset.ctRenderedCurrency;
    }

    const base = Number(el.dataset.ctBaseCents);
    if (!Number.isFinite(base)) return;

    if (activeCurrency === shopCurrency) {
      if (el.dataset.ctRenderedCurrency && el.dataset.ctOriginalHtml) {
        el.innerHTML = el.dataset.ctOriginalHtml;
      }
      delete el.dataset.ctRenderedText;
      delete el.dataset.ctRenderedCurrency;
      return;
    }

    const converted = convertCents(base, activeCurrency);
    if (converted === null) return;
    const rendered = formatAmount(converted, activeCurrency);
    if (currentText === rendered && el.dataset.ctRenderedCurrency === activeCurrency) return;
    el.textContent = rendered;
    el.dataset.ctRenderedText = rendered;
    el.dataset.ctRenderedCurrency = activeCurrency;
  }

  function renderPrices(root = document) {
    const core = [];
    const fallback = [];

    if (root.nodeType === 1 && root.matches?.('.ct-currency-price')) core.push(root);
    if (root.nodeType === 1 && root.matches?.(fallbackSelectors)) fallback.push(root);

    root.querySelectorAll?.('.ct-currency-price').forEach(el => core.push(el));
    root.querySelectorAll?.(fallbackSelectors).forEach(el => fallback.push(el));

    core.forEach(renderCorePrice);
    fallback.forEach(el => {
      if (!el.closest('.ct-currency-price')) renderFallbackPrice(el);
    });
  }

  function syncSelectors() {
    document.querySelectorAll('.ct-currency-selector').forEach(select => {
      select.value = activeCurrency;
      if (select.value !== activeCurrency) select.value = shopCurrency;
    });
  }

  function setCurrency(code) {
    code = String(code || '').toUpperCase();
    if (!supported.includes(code)) code = shopCurrency;
    activeCurrency = code;
    saveCurrency(code);
    syncSelectors();
    renderPrices(document);
  }

  function init() {
    if (!window.Currency || typeof window.Currency.convert !== 'function') {
      console.warn('[Comptoir Templier] Currency conversion library unavailable.');
      return;
    }

    activeCurrency = getSavedCurrency() || defaultCurrency;
    if (!supported.includes(activeCurrency)) activeCurrency = shopCurrency;

    document.querySelectorAll('.ct-currency-selector').forEach(select => {
      select.addEventListener('change', event => setCurrency(event.target.value));
    });

    syncSelectors();
    renderPrices(document);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) renderPrices(node);
          });
          if (mutation.target?.nodeType === 1) renderPrices(mutation.target);
        } else if (mutation.type === 'characterData' && mutation.target.parentElement) {
          renderPrices(mutation.target.parentElement);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();